import {CountryCode} from "../reducers/ticket";
import { useContext } from "react";
import { TicketDetailsContext } from '../components/TicketDetailsContext';


export const useAvailableLanguages = (): string[] => {
  // Try to get the market from context (this might throw an exception if used outside the provider)
  const { market : contextMarket} = useContext(TicketDetailsContext);
  return marketConfigs[contextMarket].supportedLanguages || ["de"];
};

export interface MarketConfig {
  code: CountryCode;
  supportedLanguages: string[];
  links: {
    [key: string]: {
      [lang: string]: string;
    };
  };
}

// Define configuration for each market
const marketConfigs: Record<CountryCode, MarketConfig> = {
  CH: {
    code: CountryCode.CH,
    supportedLanguages: ["en", "fr", "de", "it"],
    links: {
      firstLink: {
        en: "https://mybeppo.ch/en/general-terms-and-conditions/",
        fr: "https://mybeppo.ch/fr/cgv/",
        de: "https://mybeppo.ch/agb/",
        it: "https://mybeppo.ch/it/cgc/"
      },
      secondLink: {
        en: "https://mybeppo.ch/en/privacy-policy/",
        fr: "https://mybeppo.ch/fr/privacy-policy/",
        de: "https://mybeppo.ch/datenschutzerklaerung/",
        it: "https://mybeppo.ch/it/informativa-sulla-privacy/"
      }
    }
  },
  DE: {
    code: CountryCode.DE,
    supportedLanguages: ["en", "de"],
    links: {
      firstLink: {
        en: "https://mybeppo.de/gtc/",
        de: "https://mybeppo.de/agb/"
      },
      secondLink: {
        en: "https://mybeppo.de/privacy-policy/",
        de: "https://mybeppo.de/datenschutzerklarung/",
      }
    }
  },
  AT: {
    code: CountryCode.AT,
    supportedLanguages: ["en", "de"],
    links: {
      firstLink: {
        en: "https://mybeppo.at/gtc/",
        de: "https://mybeppo.at/agb/"
      },
      secondLink: {
        en: "https://mybeppo.at/privacy-policy/",
        de: "https://mybeppo.at/datenschutzerklaerung/",
      }
    }
  }
};

/**
 * Determines the market based on the hostname
 * @returns The market code (CH, DE, AT)
 */
export function detectMarket(): CountryCode {
  const hostname = window.location.hostname;

  if (hostname.includes('.ch') || hostname.includes('mybeppo.ch')) {
    return CountryCode.CH;
  } else if (hostname.includes('.de') || hostname.includes('mybeppo.de')) {
    return CountryCode.DE;
  } else if (hostname.includes('.at') || hostname.includes('mybeppo.at')) {
    return CountryCode.AT;
  }

  // Default to CH if can't detect
  return CountryCode.CH;
}

/**
 * Gets market configuration for the provided market code
 * @param marketCode The market code
 * @returns The market configuration
 */
export function getMarketConfig(marketCode: CountryCode): MarketConfig {
  return marketConfigs[marketCode];
}

/**
 * Gets a link for the specific market and language
 * @param marketCode The market code
 * @param linkKey The link key (e.g., "firstLink", "secondLink")
 * @param language The language code
 * @returns The URL for the requested link
 */
export function getMarketLink(marketCode: CountryCode, linkKey: string, language: string): string {
  const market = marketConfigs[marketCode];

  if (!market || !market.links[linkKey]) {
    console.warn(`Link "${linkKey}" not defined for market "${marketCode}"`);
    return "";
  }

  // If language not supported, fall back to first available language
  if (!market.links[linkKey][language]) {
    const fallbackLang = market.supportedLanguages[0];
    console.warn(`Language "${language}" not supported for link "${linkKey}" in market "${marketCode}", falling back to "${fallbackLang}"`);
    return market.links[linkKey][fallbackLang];
  }

  return market.links[linkKey][language];
}